import type { ReactNode } from "react";
import { ArrowDown, ArrowUp, Plus, Trash2 } from "lucide-react";
import type { SpecListEntry } from "@shared-core/data/catalogue-types";
import {
  MAX_DESCRIPTION_LENGTH,
  MAX_RELATED_ITEMS,
  MAX_SPEC_LABEL_LENGTH,
  MAX_SPEC_LIST_ENTRIES,
  MAX_SPEC_VALUE_LENGTH,
  moveListEntry,
  type InheritedCopy,
} from "@shared-core/domain/catalogue-merchandising";
import { Button } from "../../primitives/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../primitives/card";
import { Input } from "../../primitives/input";
import { Textarea } from "../../primitives/textarea";

/** What the item itself sets: the three customer-facing fields staff write. */
export interface ItemMerchandising {
  description: string;
  specList: SpecListEntry[];
  relatedItemIds: string[];
}

/** An item in the same catalogue that staff may offer as "Complete the set". */
export interface RelatedItemChoice {
  id: string;
  name: string;
}

export interface ItemMerchandisingEditorProps {
  /** The item's own values. Empty means the item inherits (when its parent has any). */
  value: ItemMerchandising;
  onChange: (next: ItemMerchandising) => void;
  /** The parent's copy, shown as a placeholder only; never written back to the item. */
  inherited: InheritedCopy | null;
  /** Other saved items in this catalogue. Never the item itself, never an unsaved draft. */
  relatedChoices: RelatedItemChoice[];
  /** Error messages from the last save, keyed by request-body path (`spec_list.0.label`). */
  errors?: Record<string, string>;
}

const FIELD_CLASS =
  "rounded-md border border-border bg-background px-3 py-1.5 text-sm aria-invalid:border-destructive";

function Counter({ used, max, id }: { used: number; max: number; id: string }) {
  const over = used > max;
  return (
    <span
      id={id}
      className={`text-xs tabular-nums ${over ? "text-destructive" : "text-muted-foreground"}`}
    >
      {used} / {max}
    </span>
  );
}

function FieldError({ id, message }: { id: string; message?: string }) {
  if (!message) return null;
  return (
    <p id={id} role="alert" className="text-sm text-red-600">
      {message}
    </p>
  );
}

function SectionHeading({
  htmlFor,
  title,
  hint,
  counter,
}: {
  htmlFor?: string;
  title: string;
  hint: ReactNode;
  counter: ReactNode;
}) {
  return (
    <div className="space-y-0.5">
      <div className="flex items-center justify-between gap-2">
        {htmlFor ? (
          <label htmlFor={htmlFor} className="text-sm font-medium">
            {title}
          </label>
        ) : (
          <p className="text-sm font-medium">{title}</p>
        )}
        {counter}
      </div>
      <p className="text-xs text-muted-foreground">{hint}</p>
    </div>
  );
}

export function ItemMerchandisingEditor({
  value,
  onChange,
  inherited,
  relatedChoices,
  errors = {},
}: ItemMerchandisingEditorProps) {
  const { description, specList, relatedItemIds } = value;
  const inheritedDescription =
    !description && inherited?.description ? inherited.description : null;
  const inheritedSpecList =
    specList.length === 0 && inherited && inherited.specList.length > 0
      ? inherited.specList
      : null;

  const setSpecEntry = (index: number, patch: Partial<SpecListEntry>) =>
    onChange({
      ...value,
      specList: specList.map((e, i) => (i === index ? { ...e, ...patch } : e)),
    });

  const choiceName = (id: string) =>
    relatedChoices.find((c) => c.id === id)?.name ?? null;
  const addableChoices = relatedChoices.filter(
    (c) => !relatedItemIds.includes(c.id),
  );

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Customer product page</CardTitle>
        <p className="text-sm text-muted-foreground">
          Customers see this on the product page.
        </p>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Description */}
        <div className="space-y-2">
          <SectionHeading
            htmlFor="merch-description"
            title="Description"
            hint="A short paragraph about the product. Start a new line for a new paragraph."
            counter={
              <Counter
                id="merch-description-count"
                used={description.length}
                max={MAX_DESCRIPTION_LENGTH}
              />
            }
          />
          <Textarea
            id="merch-description"
            rows={4}
            value={description}
            placeholder={inheritedDescription ?? undefined}
            aria-invalid={errors.description ? true : undefined}
            aria-describedby={
              [
                "merch-description-count",
                inheritedDescription ? "merch-description-inherited" : null,
                errors.description ? "merch-description-error" : null,
              ]
                .filter(Boolean)
                .join(" ") || undefined
            }
            onChange={(e) =>
              onChange({ ...value, description: e.target.value })
            }
          />
          {inheritedDescription && inherited && (
            <p
              id="merch-description-inherited"
              className="text-xs text-muted-foreground"
            >
              Inherited from {inherited.fromName}. Write your own to replace it;
              clear it to inherit again.
            </p>
          )}
          <FieldError
            id="merch-description-error"
            message={errors.description}
          />
        </div>

        {/* Spec list */}
        <div className="space-y-2">
          <SectionHeading
            title="Spec list"
            hint={`Facts such as "Size: A5". A label up to ${MAX_SPEC_LABEL_LENGTH} characters and a value up to ${MAX_SPEC_VALUE_LENGTH}.`}
            counter={
              <Counter
                id="merch-spec-count"
                used={specList.length}
                max={MAX_SPEC_LIST_ENTRIES}
              />
            }
          />
          {inheritedSpecList && inherited && (
            <div
              id="merch-spec-inherited"
              className="rounded-md border border-dashed border-border p-3 text-sm text-muted-foreground"
            >
              <p className="mb-1 text-xs">
                Inherited from {inherited.fromName}. Add a row to write your own
                list instead.
              </p>
              <dl className="grid grid-cols-[max-content_1fr] gap-x-4 gap-y-0.5">
                {inheritedSpecList.map((e, i) => (
                  <div key={`${e.label}-${i}`} className="contents">
                    <dt>{e.label}</dt>
                    <dd>{e.value}</dd>
                  </div>
                ))}
              </dl>
            </div>
          )}
          <ul className="space-y-2" aria-label="Spec list rows">
            {specList.map((entry, index) => {
              const labelError = errors[`spec_list.${index}.label`];
              const valueError = errors[`spec_list.${index}.value`];
              const rowNumber = index + 1;
              return (
                <li key={index} className="space-y-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <Input
                      className="w-40"
                      aria-label={`Spec ${rowNumber} label`}
                      placeholder="Label"
                      value={entry.label}
                      aria-invalid={labelError ? true : undefined}
                      aria-describedby={
                        labelError
                          ? `merch-spec-${index}-label-error`
                          : undefined
                      }
                      onChange={(e) =>
                        setSpecEntry(index, { label: e.target.value })
                      }
                    />
                    <Input
                      className="min-w-48 flex-1"
                      aria-label={`Spec ${rowNumber} value`}
                      placeholder="Value"
                      value={entry.value}
                      aria-invalid={valueError ? true : undefined}
                      aria-describedby={
                        valueError
                          ? `merch-spec-${index}-value-error`
                          : undefined
                      }
                      onChange={(e) =>
                        setSpecEntry(index, { value: e.target.value })
                      }
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      aria-label={`Move spec ${rowNumber} up`}
                      disabled={index === 0}
                      onClick={() =>
                        onChange({
                          ...value,
                          specList: moveListEntry(specList, index, index - 1),
                        })
                      }
                    >
                      <ArrowUp className="size-4" />
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      aria-label={`Move spec ${rowNumber} down`}
                      disabled={index === specList.length - 1}
                      onClick={() =>
                        onChange({
                          ...value,
                          specList: moveListEntry(specList, index, index + 1),
                        })
                      }
                    >
                      <ArrowDown className="size-4" />
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      aria-label={`Remove spec ${rowNumber}`}
                      onClick={() =>
                        onChange({
                          ...value,
                          specList: specList.filter((_, i) => i !== index),
                        })
                      }
                    >
                      <Trash2 className="size-4" />
                    </Button>
                  </div>
                  <FieldError
                    id={`merch-spec-${index}-label-error`}
                    message={labelError}
                  />
                  <FieldError
                    id={`merch-spec-${index}-value-error`}
                    message={valueError}
                  />
                </li>
              );
            })}
          </ul>
          <FieldError id="merch-spec-error" message={errors.spec_list} />
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={specList.length >= MAX_SPEC_LIST_ENTRIES}
            aria-describedby="merch-spec-count"
            onClick={() =>
              onChange({
                ...value,
                specList: [...specList, { label: "", value: "" }],
              })
            }
          >
            <Plus className="size-4" /> Add a spec
          </Button>
        </div>

        {/* Related items */}
        <div className="space-y-2">
          <SectionHeading
            htmlFor="merch-related-add"
            title="Related items"
            hint={
              <>
                Other items from this catalogue. Customers see them under
                &ldquo;Complete the set&rdquo;, in this order.
              </>
            }
            counter={
              <Counter
                id="merch-related-count"
                used={relatedItemIds.length}
                max={MAX_RELATED_ITEMS}
              />
            }
          />
          <ol className="space-y-2" aria-label="Related items">
            {relatedItemIds.map((id, index) => {
              const name = choiceName(id);
              const error = errors[`related_item_ids.${index}`];
              const label = name ?? "Unknown item";
              return (
                <li key={id} className="space-y-1">
                  <div className="flex items-center gap-2">
                    <span className="flex-1 truncate rounded-md border border-border bg-muted/40 px-3 py-1.5 text-sm">
                      {index + 1}. {label}
                    </span>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      aria-label={`Move ${label} up`}
                      disabled={index === 0}
                      onClick={() =>
                        onChange({
                          ...value,
                          relatedItemIds: moveListEntry(
                            relatedItemIds,
                            index,
                            index - 1,
                          ),
                        })
                      }
                    >
                      <ArrowUp className="size-4" />
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      aria-label={`Move ${label} down`}
                      disabled={index === relatedItemIds.length - 1}
                      onClick={() =>
                        onChange({
                          ...value,
                          relatedItemIds: moveListEntry(
                            relatedItemIds,
                            index,
                            index + 1,
                          ),
                        })
                      }
                    >
                      <ArrowDown className="size-4" />
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      aria-label={`Remove ${label}`}
                      aria-describedby={
                        error ? `merch-related-${index}-error` : undefined
                      }
                      onClick={() =>
                        onChange({
                          ...value,
                          relatedItemIds: relatedItemIds.filter(
                            (_, i) => i !== index,
                          ),
                        })
                      }
                    >
                      <Trash2 className="size-4" />
                    </Button>
                  </div>
                  <FieldError
                    id={`merch-related-${index}-error`}
                    message={error}
                  />
                </li>
              );
            })}
          </ol>
          <FieldError
            id="merch-related-error"
            message={errors.related_item_ids}
          />
          <select
            id="merch-related-add"
            className={FIELD_CLASS}
            value=""
            disabled={
              relatedItemIds.length >= MAX_RELATED_ITEMS ||
              addableChoices.length === 0
            }
            aria-describedby="merch-related-count"
            onChange={(e) => {
              if (!e.target.value) return;
              onChange({
                ...value,
                relatedItemIds: [...relatedItemIds, e.target.value],
              });
            }}
          >
            <option value="">
              {relatedItemIds.length >= MAX_RELATED_ITEMS
                ? "Limit reached"
                : addableChoices.length === 0
                  ? "No other saved items to add"
                  : "Add a related item"}
            </option>
            {addableChoices.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </div>
      </CardContent>
    </Card>
  );
}
